import React, { useEffect, useState, useCallback } from 'react';
import {
  Plus, Trash2, Edit3, RefreshCw, X, Loader2, FormInput, GripVertical,
  Eye, Inbox, Copy, Check, ChevronUp, ChevronDown,
} from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { PageHeader, AdminCard, Loading, Empty, Btn, Input, Select, Textarea } from './shared';
import { useToast } from '../../hooks/use-toast';

/**
 * AdminForms — custom form builder.
 *
 * Define forms (slug + fields), embed them anywhere with
 * <CustomForm slug="..."/> on the public side, and review submissions.
 */
const FIELD_TYPES = ['text', 'email', 'tel', 'number', 'textarea', 'select', 'checkbox'];

const BLANK_FORM = {
  slug: '', title: '', description: '',
  fields: [{ key: 'name', label: 'Name', type: 'text', required: true, options: [], placeholder: '' }],
  submit_label: 'Submit', success_message: "Thanks — we'll be in touch.", notify: true, enabled: true,
};

function slugify(s) {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').substring(0, 60);
}

export default function AdminForms() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [subsFor, setSubsFor] = useState(null);   // { form, items }
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems((await api.get('/api/admin/forms')).data?.items || []);
    } catch (err) { toast({ title: 'Failed to load forms', description: formatApiError(err) }); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.title?.trim()) return toast({ title: 'Title required' });
    const slug = editing.slug?.trim() || slugify(editing.title);
    const body = { ...editing, slug };
    setSaving(true);
    try {
      if (editing._existing) await api.put(`/api/admin/forms/${slug}`, body);
      else await api.post('/api/admin/forms', body);
      toast({ title: 'Saved' });
      setEditing(null);
      load();
    } catch (err) { toast({ title: 'Save failed', description: formatApiError(err) }); }
    finally { setSaving(false); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete form "${f.title}" and all its submissions?`)) return;
    try { await api.delete(`/api/admin/forms/${f.slug}`); load(); toast({ title: 'Deleted' }); }
    catch (err) { toast({ title: 'Delete failed', description: formatApiError(err) }); }
  };

  const viewSubs = async (f) => {
    try {
      const r = await api.get(`/api/admin/forms/${f.slug}/submissions`);
      setSubsFor({ form: f, items: r.data?.items || [] });
    } catch (err) { toast({ title: 'Failed to load submissions', description: formatApiError(err) }); }
  };

  // ---- field editing helpers ----
  const updateField = (i, patch) => setEditing((e) => ({ ...e, fields: e.fields.map((f, idx) => idx === i ? { ...f, ...patch } : f) }));
  const addField = () => setEditing((e) => ({ ...e, fields: [...e.fields, { key: `field_${e.fields.length + 1}`, label: '', type: 'text', required: false, options: [], placeholder: '' }] }));
  const removeField = (i) => setEditing((e) => ({ ...e, fields: e.fields.filter((_, idx) => idx !== i) }));
  const moveField = (i, dir) => setEditing((e) => {
    const next = [...e.fields]; const t = i + dir;
    if (t < 0 || t >= next.length) return e;
    [next[i], next[t]] = [next[t], next[i]];
    return { ...e, fields: next };
  });

  const copyEmbed = (f) => {
    navigator.clipboard.writeText(`<CustomForm slug="${f.slug}" />`);
    setCopied(f.slug); setTimeout(() => setCopied(null), 1500);
  };

  return (
    <>
      <PageHeader
        title={<span className="flex items-center gap-2"><FormInput size={18} /> Forms</span>}
        subtitle="Build custom forms, embed them on any page, collect submissions"
        action={
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={load}><RefreshCw size={14} /> Refresh</Btn>
            <Btn onClick={() => setEditing({ ...BLANK_FORM })}><Plus size={14} /> New form</Btn>
          </div>
        }
      />

      {loading ? <Loading /> : items.length === 0 ? (
        <AdminCard><Empty>No custom forms yet. Create one and embed it with &lt;CustomForm slug="…" /&gt;.</Empty></AdminCard>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {items.map((f) => (
            <AdminCard key={f.id}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-bold text-white truncate">{f.title}</p>
                  <p className="text-xs text-slate-400">/{f.slug} · {f.fields?.length || 0} fields · {f.enabled ? <span className="text-emerald-300">enabled</span> : <span className="text-amber-300">disabled</span>}</p>
                </div>
                <span className="inline-flex items-center gap-1 text-xs font-bold text-violet-200 bg-violet-500/15 border border-violet-500/30 rounded-lg px-2 py-1 flex-shrink-0">
                  <Inbox size={11} /> {f.submission_count}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                <Btn size="sm" variant="ghost" onClick={() => setEditing({ ...f, _existing: true })}><Edit3 size={12} /> Edit</Btn>
                <Btn size="sm" variant="ghost" onClick={() => viewSubs(f)}><Inbox size={12} /> Submissions</Btn>
                <Btn size="sm" variant="ghost" onClick={() => copyEmbed(f)}>{copied === f.slug ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />} Embed</Btn>
                <Btn size="sm" variant="danger" onClick={() => remove(f)}><Trash2 size={12} /></Btn>
              </div>
            </AdminCard>
          ))}
        </div>
      )}

      {/* ===== Builder drawer ===== */}
      {editing && (
        <div className="fixed inset-0 z-50 flex" onClick={() => !saving && setEditing(null)}>
          <div className="flex-1 bg-black/60 backdrop-blur-sm" />
          <aside className="w-full max-w-2xl bg-slate-900 border-l border-white/10 p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-white">{editing._existing ? 'Edit form' : 'New form'}</h3>
              <button onClick={() => setEditing(null)} className="p-2 rounded-lg hover:bg-white/5"><X size={18} /></button>
            </div>

            <div className="space-y-3">
              <Input label="Title *" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <Input label="Slug" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="auto from title" disabled={editing._existing} />
                <Input label="Submit button label" value={editing.submit_label} onChange={(e) => setEditing({ ...editing, submit_label: e.target.value })} />
              </div>
              <Textarea label="Description" rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              <Textarea label="Success message" rows={2} value={editing.success_message} onChange={(e) => setEditing({ ...editing, success_message: e.target.value })} />
              <div className="flex gap-6">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!editing.enabled} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} className="w-4 h-4 accent-fuchsia-500" />
                  <span className="text-sm text-slate-300">Enabled</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!editing.notify} onChange={(e) => setEditing({ ...editing, notify: e.target.checked })} className="w-4 h-4 accent-fuchsia-500" />
                  <span className="text-sm text-slate-300">Email me on submit</span>
                </label>
              </div>

              {/* Fields */}
              <div className="pt-3 border-t border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-bold text-slate-200">Fields</p>
                  <Btn size="sm" variant="ghost" onClick={addField}><Plus size={12} /> Add field</Btn>
                </div>
                <div className="space-y-2">
                  {editing.fields.map((f, i) => (
                    <div key={i} className="rounded-xl bg-white/5 border border-white/10 p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex flex-col">
                          <button onClick={() => moveField(i, -1)} className="text-slate-500 hover:text-white"><ChevronUp size={12} /></button>
                          <button onClick={() => moveField(i, 1)} className="text-slate-500 hover:text-white"><ChevronDown size={12} /></button>
                        </div>
                        <Input label="" value={f.label} onChange={(e) => updateField(i, { label: e.target.value, key: f.key || slugify(e.target.value) })} placeholder="Field label" className="flex-1" />
                        <select value={f.type} onChange={(e) => updateField(i, { type: e.target.value })} className="px-2 py-2 rounded-lg bg-slate-800/80 border border-white/10 text-white text-sm">
                          {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <button onClick={() => removeField(i)} className="p-2 rounded-lg text-rose-300 hover:bg-rose-500/10"><Trash2 size={14} /></button>
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <Input label="" value={f.key} onChange={(e) => updateField(i, { key: slugify(e.target.value) })} placeholder="key" className="w-28 text-xs font-mono" />
                        <Input label="" value={f.placeholder} onChange={(e) => updateField(i, { placeholder: e.target.value })} placeholder="placeholder" className="flex-1" />
                        {f.type === 'select' && (
                          <Input label="" value={(f.options || []).join(', ')} onChange={(e) => updateField(i, { options: e.target.value.split(',').map((o) => o.trim()).filter(Boolean) })} placeholder="opt1, opt2, opt3" className="flex-1" />
                        )}
                        <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs text-slate-300">
                          <input type="checkbox" checked={!!f.required} onChange={(e) => updateField(i, { required: e.target.checked })} className="w-3.5 h-3.5 accent-fuchsia-500" /> required
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-white/10 flex gap-2 justify-end">
              <Btn variant="ghost" onClick={() => setEditing(null)} disabled={saving}>Cancel</Btn>
              <Btn onClick={save} disabled={saving}>{saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : 'Save form'}</Btn>
            </div>
          </aside>
        </div>
      )}

      {/* ===== Submissions drawer ===== */}
      {subsFor && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setSubsFor(null)}>
          <div className="flex-1 bg-black/60 backdrop-blur-sm" />
          <aside className="w-full max-w-xl bg-slate-900 border-l border-white/10 p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-white">{subsFor.form.title} — {subsFor.items.length} submissions</h3>
              <button onClick={() => setSubsFor(null)} className="p-2 rounded-lg hover:bg-white/5"><X size={18} /></button>
            </div>
            {subsFor.items.length === 0 ? (
              <Empty>No submissions yet.</Empty>
            ) : (
              <div className="space-y-2">
                {subsFor.items.map((s) => (
                  <div key={s.id} className="rounded-xl bg-white/5 border border-white/10 p-3">
                    <p className="text-[11px] text-slate-400 mb-2">{new Date(s.created_at).toLocaleString()}{s.ip ? ` · ${s.ip}` : ''}</p>
                    <dl className="space-y-1">
                      {Object.entries(s.data || {}).map(([k, v]) => (
                        <div key={k} className="flex gap-2 text-xs">
                          <dt className="font-semibold text-slate-400 min-w-[100px]">{k}</dt>
                          <dd className="text-slate-200 break-words">{String(v)}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ))}
              </div>
            )}
          </aside>
        </div>
      )}
    </>
  );
}
